import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminDesignCaseApi } from '../../services/admin/adminDesignCaseApi';
import toast from 'react-hot-toast';
import { 
  Palette, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  Calendar,
  DollarSign,
  RefreshCw,
  Download,
  Plus,
  AlertCircle,
  Check,
  X
} from 'lucide-react';

const AdminDesignCaseManagement = () => {
  const { token } = useAuth();
  const [designCases, setDesignCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve', 'reject', 'delete'
  const [actionNote, setActionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    rejected: 0,
    cancelled: 0,
    low: 0,
    medium: 0,
    high: 0,
    urgent: 0
  });

  // Load design cases
  const loadDesignCases = async () => {
    try {
      setLoading(true);
      const response = await adminDesignCaseApi.getAllDesignCases(token);
      
      if (response.data && response.data.success) {
        const cases = response.data.data || [];
        setDesignCases(cases);
        setFilteredCases(cases);
        calculateStatistics(cases);
      } else {
        toast.error('Có lỗi xảy ra khi tải danh sách design cases');
      }
    } catch (error) {
      console.error('Error loading design cases:', error);
      toast.error('Có lỗi xảy ra khi tải danh sách design cases');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const calculateStatistics = (cases) => {
    const stats = {
      total: cases.length,
      pending: cases.filter(c => c.status === 'pending').length,
      inProgress: cases.filter(c => c.status === 'in_progress').length,
      completed: cases.filter(c => c.status === 'completed').length,
      rejected: cases.filter(c => c.status === 'rejected').length,
      cancelled: cases.filter(c => c.status === 'cancelled').length,
      low: cases.filter(c => c.priority === 'low').length,
      medium: cases.filter(c => c.priority === 'medium').length,
      high: cases.filter(c => c.priority === 'high').length,
      urgent: cases.filter(c => c.priority === 'urgent').length
    };
    setStatistics(stats);
  };

  // Filter cases
  const filterCases = () => {
    let filtered = designCases;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(case_ => 
        case_.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.order_id?.toString().includes(searchTerm) ||
        case_.seller_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(case_ => case_.status === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(case_ => case_.priority === priorityFilter);
    }

    setFilteredCases(filtered);
  };

  // Handle search and filter changes
  useEffect(() => {
    filterCases();
  }, [searchTerm, statusFilter, priorityFilter, designCases]);

  // Load data on mount
  useEffect(() => {
    loadDesignCases();
  }, [token]);

  // Handle case actions
  const handleCaseAction = async () => {
    if (!selectedCase || !actionType) return;

    try {
      setSubmitting(true);
      let response;

      switch (actionType) {
        case 'approve':
          response = await adminDesignCaseApi.forceApproveCase(token, selectedCase.id, {
            note: actionNote
          });
          break;
        case 'reject':
          response = await adminDesignCaseApi.forceRejectCase(token, selectedCase.id, {
            note: actionNote
          });
          break;
        case 'delete':
          response = await adminDesignCaseApi.deleteDesignCase(token, selectedCase.id);
          break;
        default:
          return;
      }

      if (response.data && response.data.success) {
        toast.success(`Đã ${actionType === 'approve' ? 'duyệt' : actionType === 'reject' ? 'từ chối' : 'xóa'} design case thành công!`);
        setShowActionModal(false);
        setSelectedCase(null);
        setActionType('');
        setActionNote('');
        await loadDesignCases();
      } else {
        toast.error('Có lỗi xảy ra khi thực hiện thao tác');
      }
    } catch (error) {
      console.error('Error performing case action:', error);
      toast.error('Có lỗi xảy ra khi thực hiện thao tác');
    } finally {
      setSubmitting(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: X }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status === 'pending' ? 'Chờ xử lý' :
         status === 'in_progress' ? 'Đang thực hiện' :
         status === 'completed' ? 'Hoàn thành' :
         status === 'rejected' ? 'Từ chối' :
         status === 'cancelled' ? 'Đã hủy' : status}
      </span>
    );
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { color: 'bg-green-100 text-green-800', text: 'Thấp' },
      medium: { color: 'bg-yellow-100 text-yellow-800', text: 'Trung bình' },
      high: { color: 'bg-orange-100 text-orange-800', text: 'Cao' },
      urgent: { color: 'bg-red-100 text-red-800', text: 'Khẩn cấp' }
    };

    const config = priorityConfig[priority] || priorityConfig.medium;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  // Format currency
  const formatCurrency = (amount, currency = 'VND') => {
    if (!amount) return '0';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: currency
    }).format(parseFloat(amount));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Quản lý Design Cases</h1>
              <p className="text-gray-600 mt-2">Xem và quản lý tất cả design cases trong hệ thống</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={loadDesignCases}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Làm mới</span>
              </button>
              <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Xuất báo cáo</span>
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-10 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <Palette className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Tổng cộng</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-2">{statistics.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <span className="text-sm font-medium text-gray-600">Chờ xử lý</span>
            </div>
            <p className="text-2xl font-bold text-yellow-600 mt-2">{statistics.pending}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Đang thực hiện</span>
            </div>
            <p className="text-2xl font-bold text-blue-600 mt-2">{statistics.inProgress}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-600">Hoàn thành</span>
            </div>
            <p className="text-2xl font-bold text-green-600 mt-2">{statistics.completed}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm font-medium text-gray-600">Từ chối</span>
            </div>
            <p className="text-2xl font-bold text-red-600 mt-2">{statistics.rejected}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <X className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">Đã hủy</span>
            </div>
            <p className="text-2xl font-bold text-gray-600 mt-2">{statistics.cancelled}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium text-gray-600">Thấp</span>
            </div>
            <p className="text-2xl font-bold text-green-500 mt-2">{statistics.low}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              <span className="text-sm font-medium text-gray-600">Trung bình</span>
            </div>
            <p className="text-2xl font-bold text-yellow-500 mt-2">{statistics.medium}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              <span className="text-sm font-medium text-gray-600">Cao</span>
            </div>
            <p className="text-2xl font-bold text-orange-500 mt-2">{statistics.high}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm font-medium text-gray-600">Khẩn cấp</span>
            </div>
            <p className="text-2xl font-bold text-red-500 mt-2">{statistics.urgent}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow border mb-6">
          <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm theo tiêu đề, order ID, seller, customer, product..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="lg:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tất cả trạng thái</option>
                <option value="pending">Chờ xử lý</option>
                <option value="in_progress">Đang thực hiện</option>
                <option value="completed">Hoàn thành</option>
                <option value="rejected">Từ chối</option>
                <option value="cancelled">Đã hủy</option>
              </select>
            </div>
            <div className="lg:w-48">
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tất cả độ ưu tiên</option>
                <option value="low">Thấp</option>
                <option value="medium">Trung bình</option>
                <option value="high">Cao</option>
                <option value="urgent">Khẩn cấp</option>
              </select>
            </div>
          </div>
        </div>

        {/* Cases Table */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thông tin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Seller
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Designer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chi phí
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Độ ưu tiên
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ngày tạo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCases.map((case_) => (
                  <tr key={case_.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {case_.title || `Design Case #${case_.id}`}
                        </div>
                        <div className="text-sm text-gray-500">
                          Order: #{case_.order_id}
                        </div>
                        {case_.customer_name && (
                          <div className="text-xs text-gray-400">
                            Customer: {case_.customer_name}
                          </div>
                        )}
                        {case_.product_name && (
                          <div className="text-xs text-gray-400">
                            Product: {case_.product_name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {case_.seller_name || 'N/A'}
                        </span>
                      </div>
                      {case_.seller_email && (
                        <div className="text-xs text-gray-500 mt-1">
                          {case_.seller_email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {case_.designer_name || 'Chưa phân công'}
                        </span>
                      </div>
                      {case_.designer_email && (
                        <div className="text-xs text-gray-500 mt-1">
                          {case_.designer_email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 text-gray-400 mr-1" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(case_.cost_per_design)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPriorityBadge(case_.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(case_.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {formatDate(case_.created_at)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedCase(case_);
                            setShowDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Xem chi tiết"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {case_.status === 'pending' && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedCase(case_);
                                setActionType('approve');
                                setShowActionModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                              title="Duyệt"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCase(case_);
                                setActionType('reject');
                                setShowActionModal(true);
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Từ chối"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            setSelectedCase(case_);
                            setActionType('delete');
                            setShowActionModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Xóa"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredCases.length === 0 && (
            <div className="text-center py-12">
              <Palette className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Không có design cases nào</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Chi tiết Design Case</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ID</label>
                    <p className="text-sm text-gray-900">{selectedCase.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Order ID</label>
                    <p className="text-sm text-gray-900">{selectedCase.order_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tiêu đề</label>
                    <p className="text-sm text-gray-900">{selectedCase.title || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                    <div className="mt-1">{getStatusBadge(selectedCase.status)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Product ID</label>
                    <p className="text-sm text-gray-900">{selectedCase.product_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Product Name</label>
                    <p className="text-sm text-gray-900">{selectedCase.product_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priority</label>
                    <p className="text-sm text-gray-900">{selectedCase.priority || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Deadline</label>
                    <p className="text-sm text-gray-900">{selectedCase.deadline ? formatDate(selectedCase.deadline) : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Seller</label>
                    <p className="text-sm text-gray-900">{selectedCase.seller_name || 'N/A'}</p>
                    {selectedCase.seller_email && (
                      <p className="text-xs text-gray-500">{selectedCase.seller_email}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Designer</label>
                    <p className="text-sm text-gray-900">{selectedCase.designer_name || 'Chưa phân công'}</p>
                    {selectedCase.designer_email && (
                      <p className="text-xs text-gray-500">{selectedCase.designer_email}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Customer</label>
                    <p className="text-sm text-gray-900">{selectedCase.customer_name || 'N/A'}</p>
                    {selectedCase.customer_email && (
                      <p className="text-xs text-gray-500">{selectedCase.customer_email}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Order Total</label>
                    <p className="text-sm text-gray-900">{selectedCase.order_total ? formatCurrency(selectedCase.order_total) : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Chi phí Design</label>
                    <p className="text-sm text-gray-900">{formatCurrency(selectedCase.cost_per_design)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ngày tạo</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedCase.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ngày cập nhật</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedCase.updated_at)}</p>
                  </div>
                  {selectedCase.completed_at && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ngày hoàn thành</label>
                      <p className="text-sm text-gray-900">{formatDate(selectedCase.completed_at)}</p>
                    </div>
                  )}
                </div>
                
                {selectedCase.note && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ghi chú</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedCase.note}</p>
                  </div>
                )}

                {selectedCase.design_files && selectedCase.design_files.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Design Files</label>
                    <div className="mt-2 space-y-2">
                      {selectedCase.design_files.map((file, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <a 
                            href={file} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm underline"
                          >
                            Design File {index + 1}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {actionType === 'approve' ? 'Duyệt' : 
                   actionType === 'reject' ? 'Từ chối' : 'Xóa'} Design Case
                </h3>
                <button
                  onClick={() => setShowActionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Bạn có chắc chắn muốn {actionType === 'approve' ? 'duyệt' : 
                  actionType === 'reject' ? 'từ chối' : 'xóa'} design case này?
                </p>
                
                {(actionType === 'approve' || actionType === 'reject') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lý do {actionType === 'approve' ? 'duyệt' : 'từ chối'}
                    </label>
                    <textarea
                      value={actionNote}
                      onChange={(e) => setActionNote(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                      placeholder={`Nhập lý do ${actionType === 'approve' ? 'duyệt' : 'từ chối'}...`}
                    />
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowActionModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={handleCaseAction}
                  disabled={submitting}
                  className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2 ${
                    actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                    actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-red-600 hover:bg-red-700'
                  } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    actionType === 'approve' ? <Check className="w-4 h-4" /> :
                    actionType === 'reject' ? <X className="w-4 h-4" /> :
                    <Trash2 className="w-4 h-4" />
                  )}
                  <span>
                    {submitting ? 'Đang xử lý...' :
                     actionType === 'approve' ? 'Duyệt' :
                     actionType === 'reject' ? 'Từ chối' : 'Xóa'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDesignCaseManagement; 